import { MeasuredToken } from "./LayoutMeasurer";
import { Token } from "./BuildTokens";
import { FontMetrics } from "./CanvasKerningApplier";
import { ISplitOptions } from "../../models/text/ISplitOptions";
import { splitIntoGraphemes } from "./TextSegmentation";

export type ProcessedChar = {
  char: string;
  rect: DOMRect;
  token: Token;
  charIndexGlobal: number;
  charIndexInLine: number;
  charIndexInWord: number;
  isBeforeElement?: boolean;
  isAfterElement?: boolean;
};

export type ProcessedWord = {
  chars: ProcessedChar[];
  rect: DOMRect;
  wordIndexGlobal: number;
  wordIndexInLine: number;
  isBeforeElement?: boolean;
  isAfterElement?: boolean;
  noSpaceBefore?: boolean;
};

export type LayoutLine = {
  words: ProcessedWord[];
  rect: DOMRect;
  lineIndex: number;
  isBeforeElement?: boolean;
  isAfterElement?: boolean;
  fitFontSize?: number;
  /** Browser-accurate line width (right edge from domRect, not Canvas). Used for fit calculations. */
  fitWidth?: number;
  /** Sum of individual browser-accurate word widths (no inter-word spaces). Used for fit formula. */
  browserWordWidthSum?: number;
};

const LINE_TOLERANCE = 5;

export function SplitMeasuredTokens(
  measured: MeasuredToken[],
  container: HTMLElement,
  fontMetrics: FontMetrics,
  options: ISplitOptions,
): LayoutLine[] {
  const lines: LayoutLine[] = [];
  let currentLine: LayoutLine | null = null;
  let y = 0;

  let charIndexGlobal = 0;
  let wordIndexGlobal = 0;
  let currentLineBrowserRight = 0;

  measured.forEach((mt) => {
    const token = mt.token;
    const isBefore = token.meta?.isBeforeElement ?? false;
    const isAfter = token.meta?.isAfterElement ?? false;

    if (token.type === "br") {
      currentLine = null;
      return;
    }

    if (token.type === "text") {
      const wordText = token.content;
      const splitClass: string[] = (token.meta?.splitClass as string[]) ?? [];
      const joinPrev = !!token.meta?.joinPrev;

      const chars: ProcessedChar[] = [];
      let currentXInWord = 0;

      const graphemes = splitIntoGraphemes(wordText, options);

      for (let i = 0; i < graphemes.length; i++) {
        const ch = graphemes[i];
        const prev = i > 0 ? graphemes[i - 1] : null;

        const charWidth = fontMetrics.getCharWidth(ch);
        const kerning = prev ? fontMetrics.getKerning(prev, ch) : 0;

        currentXInWord += kerning;

        const charRect = new DOMRect(
          mt.rect.left + currentXInWord,
          mt.rect.top,
          charWidth,
          mt.rect.height
        );

        const pc: ProcessedChar = {
          char: ch,
          rect: charRect,
          token,
          charIndexInWord: i,
          charIndexInLine: 0,
          charIndexGlobal: charIndexGlobal++,
        };

        if (splitClass.length) (pc as any).splitClass = splitClass;

        chars.push(pc);
        currentXInWord += charWidth;
      }

      if (chars.length > 0) {
        const lastChar = chars[chars.length - 1];
        if (isBefore) lastChar.isBeforeElement = true;
        if (isAfter) lastChar.isAfterElement = true;
      }

      const currentY = Math.round(mt.rect.top);
      const baselineY = Math.round(y);
      const isNewLine = !currentLine || Math.abs(currentY - baselineY) > LINE_TOLERANCE;
      if (isNewLine) {
        y = currentY;
        currentLine = { words: [], rect: mt.rect, lineIndex: lines.length };
        currentLineBrowserRight = 0;
        lines.push(currentLine);
      }
      if (!currentLine) return;

      const wordBrowserRight = mt.rect.left + (mt.browserWidth ?? mt.rect.width);

      if (joinPrev && currentLine.words.length > 0) {
        const target = currentLine.words[currentLine.words.length - 1];

        const charsBeforeAll = currentLine.words.reduce((acc, w) => acc + w.chars.length, 0);
        const baseInWord = target.chars.length;

        chars.forEach((c, i) => {
          c.charIndexInLine = charsBeforeAll + i;
          c.charIndexInWord = baseInWord + i;
        });

        target.chars.push(...chars);
        target.rect = mergeRects([target.rect, mt.rect]);
        currentLine.rect = mergeRects(currentLine.words.map((w) => w.rect));
        currentLineBrowserRight = Math.max(currentLineBrowserRight, wordBrowserRight);
        currentLine.fitWidth = currentLineBrowserRight - currentLine.rect.left;
        currentLine.browserWordWidthSum = (currentLine.browserWordWidthSum ?? 0) + (mt.browserWidth ?? mt.rect.width);
        if (isBefore) target.isBeforeElement = true;
        if (isAfter) target.isAfterElement = true;
        return;
      }

      const wordIndexInLine = currentLine.words.length;
      const charsBefore = currentLine.words.reduce((acc, w) => acc + w.chars.length, 0);
      chars.forEach((c, i) => (c.charIndexInLine = charsBefore + i));

      const processedWord: ProcessedWord = {
        chars,
        rect: mt.rect,
        wordIndexGlobal: wordIndexGlobal++,
        wordIndexInLine,
        isBeforeElement: isBefore,
        isAfterElement: isAfter,
        noSpaceBefore: !!token.meta?.noSpaceBefore,
      };

      currentLine.words.push(processedWord);
      currentLine.rect = mergeRects(currentLine.words.map((w) => w.rect));
      currentLineBrowserRight = Math.max(currentLineBrowserRight, wordBrowserRight);
      currentLine.fitWidth = currentLineBrowserRight - currentLine.rect.left;
      currentLine.browserWordWidthSum = (currentLine.browserWordWidthSum ?? 0) + (mt.browserWidth ?? mt.rect.width);
      if (isBefore) currentLine.isBeforeElement = true;
      if (isAfter) currentLine.isAfterElement = true;
      return;
    }

    if (token.type === "element") {
      const rect = mt.rect;
      const currentY = Math.round(rect.top);
      const baselineY = Math.round(y);
      const isNewLine = !currentLine || Math.abs(currentY - baselineY) > LINE_TOLERANCE;

      if (isNewLine) {
        y = currentY;
        currentLine = { words: [], rect, lineIndex: lines.length };
        currentLineBrowserRight = 0;
        lines.push(currentLine);
      }
      if (!currentLine) return;

      const wordIndexInLine = currentLine.words.length;
      const charsBefore = currentLine.words.reduce((acc, w) => acc + w.chars.length, 0);

      const chars: ProcessedChar[] = [
        {
          char: "[E]",
          rect,
          token,
          charIndexInWord: 0,
          charIndexInLine: charsBefore,
          charIndexGlobal: charIndexGlobal++,
        },
      ];

      const processedWord: ProcessedWord = {
        chars,
        rect,
        wordIndexGlobal: wordIndexGlobal++,
        wordIndexInLine,
        isBeforeElement: false,
        isAfterElement: false,
      };

      currentLine.words.push(processedWord);
      currentLine.rect = mergeRects(currentLine.words.map((w) => w.rect));
      currentLineBrowserRight = Math.max(currentLineBrowserRight, mt.rect.left + (mt.browserWidth ?? mt.rect.width));
      currentLine.fitWidth = currentLineBrowserRight - currentLine.rect.left;
      currentLine.browserWordWidthSum = (currentLine.browserWordWidthSum ?? 0) + (mt.browserWidth ?? mt.rect.width);
    }
  });

  return lines;
}

function mergeRects(rects: DOMRect[]): DOMRect {
  if (rects.length === 0) return new DOMRect(0, 0, 0, 0);
  const left = Math.min(...rects.map((r) => r.left));
  const top = Math.min(...rects.map((r) => r.top));
  const right = Math.max(...rects.map((r) => r.right));
  const bottom = Math.max(...rects.map((r) => r.bottom));
  return new DOMRect(left, top, right - left, bottom - top);
}
